//Component to render URL custom field

import React, { useState } from 'react';
import { useIntl, getTranslatedText } from '@nile/components';
import {
  Link,
  TextStyle,
  TextField,
  Button,
  Popover
} from '@superops/ui-design-system';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';

const URLStylesWrapper = styled.div`
  max-width: 300px;
  .action {
    visibility: hidden;
  }
  &:hover {
    .action {
      visibility: visible;
    }
  }
  .Superops-Link__IconLockup {
    display: none !important;
  }
  .no-padding-button .Superops-Button Superops-Button--plain {
    padding: 0;
  }
  .Superops-Link--externelLinkWrapper {
    text-overflow: ellipsis;
    display: inline-block;
    overflow: hidden;
  }
`;

const EditContainerStyles = styled.div`
  padding: 20px 15px;
  min-width: 300px;
  input {
    text-overflow: ellipsis;
  }
`;

const EditContainer = (props) => {
  const intl = useIntl();

  const [urlText, setUrlText] = useState(props?.value?.text || null);
  const [url, setUrl] = useState(props?.value?.url || null);
  const [urlError, setUrlError] = useState(false);

  const isUrlValid = (str) => {
    const pattern = /^(ftp|http|https):\/\/[^ "]+$/;
    return pattern.test(str);
  };

  const onSave = () => {
    if (!url && !urlText) {
      const updatedData = {
        ...props?.field,
        text: null,
        url: null
      };
      props.onSave(updatedData);
      props.closeEdit();
    } else if (urlText && !isUrlValid(url)) {
      setUrlError(true);
      return;
    } else {
      const updatedData = {
        ...props?.field,
        text: urlText || null,
        url: url
      };
      setUrlError(false);
      props.onSave(updatedData);
      props.closeEdit();
    }
  };

  return (
    <EditContainerStyles>
      <div className="mb-10">
        <TextField
          label={getTranslatedText(intl, 'customField.url.urlText.label')}
          value={urlText}
          onChange={(value) => {
            setUrlText(value);
          }}
          type="text"
          className="mb-10"
          placeholder={getTranslatedText(
            intl,
            'customField.url.urlText.placeholder'
          )}
        />
      </div>

      <div className="mb-10">
        <TextField
          label={getTranslatedText(intl, 'customField.url.label')}
          value={url}
          onChange={(value) => setUrl(value)}
          customClassName="mb-10"
          type="text"
          error={urlError && getTranslatedText(intl, 'common.url.error')}
          placeholder={getTranslatedText(
            intl,
            'customField.url.urlLink.placeholder'
          )}
          maxLength={256}
        />
      </div>

      <div className="mt-20 flex g10">
        <Button onClick={props.closeEdit}>
          {getTranslatedText(intl, 'common.cancel')}
        </Button>

        <Button onClick={onSave} primary>
          {getTranslatedText(intl, 'common.save')}
        </Button>
      </div>
    </EditContainerStyles>
  );
};

const URLField = (props) => {
  const intl = useIntl();
  const [showEdit, setShowEdit] = useState(false);

  const getValue = (value) => {
    if (value) {
      if (typeof value === 'string') {
        return JSON.parse(value);
      } else {
        return value;
      }
    } else {
      return null;
    }
  };

  const formattedValue = getValue(props?.value);
  const displayValue =
    getValue(props?.value)?.text || getValue(props?.value)?.url || null;
  const url = getValue(props?.value)?.url;

  const label = props?.field?.label;

  const emptyStatePlaceholder = getTranslatedText(intl, 'common.link');

  const closeEdit = React.useCallback(() => {
    setShowEdit(false);
  }, []);
  return (
    <>
      <URLStylesWrapper className="url-custom-field">
        {props?.editable ? (
          <>
            {!props.hideLabel && (
              <div className="label-holder">
                <div className="mb-8">
                  <TextStyle weight="Medium">{label}</TextStyle>
                </div>
              </div>
            )}

            <Popover
              active={showEdit}
              onClose={closeEdit}
              activator={
                displayValue ? (
                  <div className="flex g10">
                    <div className="ellipsis">
                      <Link removeUnderline external url={url}>
                        {displayValue}
                      </Link>
                    </div>
                    <div
                      className="action pointer"
                      onClick={() => {
                        setShowEdit(!showEdit);
                      }}
                    >
                      <SVGIcon name="Edit" />
                    </div>
                  </div>
                ) : (
                  <div className="no-padding-button">
                    <Button
                      onClick={() => {
                        setShowEdit(!showEdit);
                      }}
                      plain
                      icon={
                        <div className="ignoreOverride">
                          <SVGIcon name="Add" />
                        </div>
                      }
                    >
                      {emptyStatePlaceholder}
                    </Button>
                  </div>
                )
              }
              zIndexOverride={9999}
              preferredAlignment="left"
            >
              <EditContainer
                closeEdit={closeEdit}
                field={props?.field}
                value={formattedValue}
                onSave={props.updateSelection}
              />
            </Popover>
          </>
        ) : (
          <>
            {displayValue ? (
              <Link external url={url}>
                {displayValue}
              </Link>
            ) : (
              <TextStyle variation="subdued">
                {getTranslatedText(intl, 'app.noData.message')}
              </TextStyle>
            )}
          </>
        )}
      </URLStylesWrapper>
    </>
  );
};

export default URLField;
